/**
 * Post-transfer setup checklist (NOS-169). Shown to a child org's members
 * after their sponsorship exits while the org still lacks its own Stripe
 * onboarding or EIN — donations stay unavailable until both are done.
 */
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { supabase } from "@/lib/supabase"

export function PostTransferBanner({ orgId }: { orgId: string }) {
  const [show, setShow] = useState(false)
  const [needsStripe, setNeedsStripe] = useState(false)
  const [needsEin, setNeedsEin] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function check() {
      const [{ data: link }, { data: org }] = await Promise.all([
        supabase
          .from("sponsorships")
          .select("status, exited_at")
          .eq("child_org_id", orgId)
          .eq("status", "exited")
          .maybeSingle(),
        supabase
          .from("organizations")
          .select("stripe_onboarding_complete, ein")
          .eq("id", orgId)
          .maybeSingle(),
      ])
      if (cancelled) return
      const missingStripe = org?.stripe_onboarding_complete !== true
      const missingEin = !org?.ein
      setNeedsStripe(missingStripe)
      setNeedsEin(missingEin)
      setShow(Boolean(link) && (missingStripe || missingEin))
    }
    check()
    return () => {
      cancelled = true
    }
  }, [orgId])

  if (!show) return null

  return (
    <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <h2 className="font-semibold text-amber-900">
        Welcome to independence — finish your setup
      </h2>
      <p className="mt-1 text-sm text-amber-800">
        Your organization now operates under its own identity. Donations are paused until you
        complete:
      </p>
      <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-amber-800">
        {needsStripe && (
          <li>
            <strong>Connect your own Stripe account</strong> — use the Stripe card below.
          </li>
        )}
        {needsEin && (
          <li>
            <strong>Enter your EIN</strong> in{" "}
            <Link to="/dashboard/settings" className="underline">
              Settings → Legal identity
            </Link>{" "}
            so receipts carry your own tax identity.
          </li>
        )}
      </ul>
    </div>
  )
}
